import React, { useCallback, useEffect, useState } from "react";
import { api, fmtDate, fmtMoney, downloadWithToast } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import DaikinPoEditorSheet from "@/pages/daikin-pos/DaikinPoEditorSheet";
import DaikinPoSettingsDialog from "@/pages/daikin-pos/DaikinPoSettingsDialog";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash, FilePdf, ArrowsClockwise, MagnifyingGlass, PencilLine, Gear } from "@phosphor-icons/react";

const STATUS_META = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  sent: { label: "Sent", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  confirmed: { label: "Confirmed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-red-50 text-red-600 border-red-200" },
};

export default function DaikinPOs() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { user } = useAuth();
  const canWrite = ["admin", "manager", "accountant"].includes(user?.role);
  const canManage = ["admin", "manager"].includes(user?.role);

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/daikin-pos", { params: { q: q || undefined, limit: 200 } }); setRows(data || []); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const del = async (id, ofn) => {
    if (!window.confirm(`Delete Daikin OFN ${ofn}?`)) return;
    try { await api.delete(`/daikin-pos/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/daikin-pos/${id}/status`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="daikin-pos-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="text-[#003a8c] font-extrabold">DAIKIN</span> Purchase Orders (OFN)
          </h1>
          <p className="text-slate-500 text-sm mt-1">Dealer Purchase Order Forwarding Notes for Daikin equipment orders, in the official format.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="h-8" onClick={load} data-testid="daikin-refresh"><ArrowsClockwise size={12} className="mr-1" /> Refresh</Button>
          {canManage && (
            <Button variant="outline" size="sm" className="h-8" onClick={() => setSettingsOpen(true)} data-testid="daikin-settings-btn"><Gear size={13} className="mr-1" /> Logo & Settings</Button>
          )}
          {canWrite && (
            <Button onClick={() => setCreating(true)} className="rounded-sm bg-[#003a8c] hover:bg-[#002a66] text-white" data-testid="new-daikin-po-btn"><Plus size={13} className="mr-1" /> New Daikin PO</Button>
          )}
        </div>
      </div>

      <div className="relative max-w-sm">
        <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search OFN no, consignee, model…" className="pl-8 h-9 text-[13px]" data-testid="daikin-search" />
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-[11px]">OFN No.</TableHead>
                <TableHead className="text-[11px]">Date</TableHead>
                <TableHead className="text-[11px]">Consignee</TableHead>
                <TableHead className="text-[11px] text-center">Status</TableHead>
                <TableHead className="text-[11px] text-center">Items</TableHead>
                <TableHead className="text-[11px] text-right">Net Sales Value</TableHead>
                <TableHead className="text-[11px] text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && !busy && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10 text-sm">No Daikin POs yet. Click “New Daikin PO” to create one.</TableCell></TableRow>
              )}
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`daikin-row-${r.id}`}>
                  <TableCell className="font-mono text-[12px] font-semibold text-[#003a8c]">{r.ofn_no}</TableCell>
                  <TableCell className="text-[12px]">{fmtDate(r.date)}</TableCell>
                  <TableCell className="text-[12px] max-w-[240px] truncate">{r.consignee_name || "—"}</TableCell>
                  <TableCell className="text-center">
                    {canWrite ? (
                      <select value={r.status || "draft"} onChange={(e) => updateStatus(r.id, e.target.value)}
                        className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 cursor-pointer ${STATUS_META[r.status || "draft"]?.cls}`}
                        data-testid={`daikin-status-${r.id}`}>
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    ) : (
                      <span className={`text-[11px] font-semibold rounded-full border px-2 py-0.5 ${STATUS_META[r.status || "draft"]?.cls}`} data-testid={`daikin-status-${r.id}`}>{STATUS_META[r.status || "draft"]?.label}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px] text-center">{(r.items || []).length}</TableCell>
                  <TableCell className="text-[12px] text-right font-mono-num font-semibold">{fmtMoney(r.grand_total || 0)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => downloadWithToast(`/daikin-pos/${r.id}/pdf`, `OFN_${r.ofn_no}.pdf`)} className="p-1.5 text-slate-500 hover:text-[#003a8c]" title="Download PDF" data-testid={`daikin-pdf-${r.id}`}><FilePdf size={15} /></button>
                      {canWrite && <button onClick={() => setEditing(r.id)} className="p-1.5 text-slate-500 hover:text-blue-600" title="Edit" data-testid={`daikin-edit-${r.id}`}><PencilLine size={15} /></button>}
                      {canManage && <button onClick={() => del(r.id, r.ofn_no)} className="p-1.5 text-slate-400 hover:text-red-500" title="Delete" data-testid={`daikin-del-${r.id}`}><Trash size={15} /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {creating && <DaikinPoEditorSheet onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <DaikinPoEditorSheet poId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <DaikinPoSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  );
}
